import React, { Component } from "react";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Action from "../../components/Action";
import Card from "../../components/Card";
import QuoteGuarantees from "../QuoteCommon/QuoteGuarantees";
import List, { ListItem } from "../../components/List";
import Details from "../../components/Details";
import { formatMoneyWithCurrency, moneyStrToNum } from "../../shared/utils/moneyUtils";
import Image from "../../components/Image";
import imageRelaxingOnSofa from "../../images/relaxing-on-sofa.png";
import ReferralCodeBanner from "../../components/ReferralCodeBanner";
import { ListItemSuffix, QuoteBreakdown, QuotedPrice, QuotedPriceWrapper } from "../QuoteCommon/QuoteStyles";
import { isConnellsReferral } from "./utils/ReferrerQuoteUtils";

class ReferrerQuoteDetailsPage extends Component {
  constructor(props) {
    super(props);

    let channel = props.defaultChannel;
    if (this.props.referral?.referralSource) {
      channel = this.props.referral.referralSource;
    }

    this.state = {
      selectedTransactionType: this.props.transactionType,
      transactionType: this.props.transactionType,
      buyingDetails: this.props.buyingDetails,
      sellingDetails: this.props.sellingDetails,
      personalDetails: this.props.personalDetails,
      channel: channel,
      agentName: this.props.agentName,
      agentEmailAddress: this.props.agentEmailAddress,
      additionalInformation: this.props.additionalInformation,
      quoteDetails: null,
      loading: true,
      error: null,
    };
  }

  handleFetchError = (err) => {
    console.error("Referrer quote generation failed:", err);
    this.setState({ error: "Unable to generate your quote", loading: false });
  };

  componentDidMount() {
    if (this.hasInitiated) return;
    this.hasInitiated = true;

    fetch("/api/partialQuote", {
      method: "PUT",
      body: JSON.stringify({
        transactionType: this.props.transactionType,
        channel: this.state.channel,
        fallbackChannel: this.props.fallbackChannel,
        sellingDetails:
          this.props.transactionType === "Selling" || this.props.transactionType === "BuyingAndSelling"
            ? this.props.sellingDetails
            : null,
        buyingDetails:
          this.props.transactionType === "Buying" || this.props.transactionType === "BuyingAndSelling"
            ? this.props.buyingDetails
            : null,
        remortgagingDetails: null,
        referralCode: this.props.referral ? this.props.referral.referralCode : null,
        googleAnalyticsId: this.props.googleAnalyticsId,
        campaign: this.props.campaign ?? null,
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const quoteData = await resp.json();
        this.setState({ quoteDetails: quoteData, loading: false });
      }, this.handleFetchError)
      .catch(this.handleFetchError);
  }

  render() {
    if (!!this.state.loading) {
      return null;
    }

    if (this.state.error) {
      return (
        <React.Fragment>
          <ScrollToTop />
          <Card title="Sorry, that didn’t work" removeTopPadding>
            {this.state.error}
          </Card>
        </React.Fragment>
      );
    }

    let saleLegalFee = "";
    let salePropertyPrice = "";
    let purchaseLegalFee = "";
    let purchasePropertyPrice = "";
    let quotePrice = "";
    let quotePriceText = "";
    let stampDuty = "";
    let stampDutyDescription = "Stamp Duty (paid to the government)";
    let landRegistryFee = "";
    let companiesHouseRegistrationFee = "";
    let totalFeesAndTaxes = "";
    let sellingTenure = "";
    let buyingTenure = "";

    const connellsCase = isConnellsReferral(this.state.channel);

    let includeDecimal = landRegistryFee.includes(".") || stampDuty.includes(".");

    if (this.state.quoteDetails) {
      quotePrice = formatMoneyWithCurrency(
        moneyStrToNum(this.state.quoteDetails.totalPrice).toString(),
        includeDecimal,
      );

      saleLegalFee = formatMoneyWithCurrency(
        moneyStrToNum(this.state.quoteDetails.sellingQuotePrice).toString(),
        includeDecimal,
      );

      purchaseLegalFee = formatMoneyWithCurrency(
        moneyStrToNum(this.state.quoteDetails.buyingQuotePrice).toString(),
        includeDecimal,
      );

      stampDuty = formatMoneyWithCurrency(
        moneyStrToNum(this.state.quoteDetails.stampDutyFee).toString(),
        includeDecimal,
      );

      if (
        this.props.buyingDetails &&
        !!this.props.buyingDetails.firstTimeBuyer &&
        !!this.props.buyingDetails.buyToLet
      ) {
        stampDutyDescription = "Stamp Duty (paid to the government, no first-time buyer relief)";
      }

      landRegistryFee = formatMoneyWithCurrency(
        moneyStrToNum(this.state.quoteDetails.landRegistryFee).toString(),
        includeDecimal,
      );

      companiesHouseRegistrationFee = formatMoneyWithCurrency(
        moneyStrToNum(this.state.quoteDetails.companiesHouseRegistrationFee),
        includeDecimal,
      );

      totalFeesAndTaxes = formatMoneyWithCurrency(
        moneyStrToNum(this.state.quoteDetails.totalFeesAndTaxes).toString(),
        includeDecimal,
      );

      purchasePropertyPrice =
        this.state.transactionType === "Buying" || this.state.transactionType === "BuyingAndSelling"
          ? formatMoneyWithCurrency(moneyStrToNum(this.props.buyingDetails.price).toString(), includeDecimal)
          : "";

      salePropertyPrice =
        this.state.transactionType === "Selling" || this.state.transactionType === "BuyingAndSelling"
          ? formatMoneyWithCurrency(moneyStrToNum(this.props.sellingDetails.price).toString(), includeDecimal)
          : "";
    }

    if (this.props.transactionType === "Selling" || this.props.transactionType === "BuyingAndSelling") {
      if (this.props.sellingDetails.tenure === "leasehold with share of freehold") {
        sellingTenure = "share of freehold";
      } else {
        sellingTenure = this.props.sellingDetails.tenure;
      }
    }

    if (this.props.transactionType === "Buying" || this.props.transactionType === "BuyingAndSelling") {
      if (this.props.buyingDetails.tenure === "leasehold with share of freehold") {
        buyingTenure = "share of freehold";
      } else {
        buyingTenure = this.props.buyingDetails.tenure;
      }
    }

    let noHiddenFeesIntro = "The price covers everything your client needs for a typical ";

    // eslint-disable-next-line default-case
    switch (this.state.transactionType) {
      case "Buying":
        quotePriceText = `That covers all our legal work on their ${buyingTenure} purchase at ${purchasePropertyPrice}, including VAT.`;
        noHiddenFeesIntro += "purchase: VAT, bank transfers, ID checks and search reports.";
        break;
      case "Selling":
        quotePriceText = `That covers all our legal work on their ${sellingTenure} sale at ${salePropertyPrice}, including VAT.`;
        noHiddenFeesIntro += "sale: VAT, bank transfers and ID checks.";
        break;
      case "BuyingAndSelling":
        quotePriceText = `That covers all our legal work on their ${buyingTenure} purchase at ${purchasePropertyPrice} and ${sellingTenure} sale at ${salePropertyPrice}, including VAT.`;
        noHiddenFeesIntro += "sale and purchase: VAT, bank transfers, ID checks and search reports.";
        break;
    }

    return (
      <React.Fragment>
        <ScrollToTop />
        <Card title="Here’s the price for your client" removeTopPadding>
          <QuotedPriceWrapper>
            <QuotedPrice>{quotePrice}</QuotedPrice>
            <p>{quotePriceText}</p>
            {!connellsCase && <QuoteGuarantees transactionType={this.state.transactionType} />}
          </QuotedPriceWrapper>

          {!!this.props.referral && !this.props.hideReferralCodeBanner && (
            <ReferralCodeBanner text={this.props.referral.quoteMessage} withinQuote={true} />
          )}

          {this.state.transactionType === "Buying" || this.state.transactionType === "BuyingAndSelling" ? (
            <QuoteBreakdown>
              <List>
                {this.state.transactionType === "BuyingAndSelling" ? (
                  <ListItem divider>
                    Juno’s fee for the {sellingTenure} sale
                    <ListItemSuffix>{saleLegalFee}</ListItemSuffix>
                  </ListItem>
                ) : (
                  <></>
                )}
                <ListItem divider>
                  Juno’s fee for the {buyingTenure} purchase
                  <ListItemSuffix>{purchaseLegalFee}</ListItemSuffix>
                </ListItem>
                <ListItem divider>
                  Land Registry fee (paid to the government)
                  <ListItemSuffix data-testid="land-registry-fee">{landRegistryFee}</ListItemSuffix>
                </ListItem>
                <ListItem divider>
                  {stampDutyDescription}
                  <ListItemSuffix data-testid="stamp-duty-fee">{stampDuty}</ListItemSuffix>
                </ListItem>
                {this.state.buyingDetails.clientIsCompany && (
                  <ListItem divider>
                    Companies house registration fee
                    <ListItemSuffix data-testid="companies-house-registration-fee">
                      {companiesHouseRegistrationFee}
                    </ListItemSuffix>
                  </ListItem>
                )}
                <ListItem divider>
                  Total, including government fees and taxes
                  <ListItemSuffix data-testid="total-fees-and-taxes">{totalFeesAndTaxes}</ListItemSuffix>
                </ListItem>
              </List>
            </QuoteBreakdown>
          ) : null}

          <Action onClick={() => this.props.onStepComplete({})}>Send this quote to my client</Action>
        </Card>

        {!connellsCase && (
          <Card title="No hidden fees" removeTopPadding headingSize="3">
            <p>{noHiddenFeesIntro}</p>

            <Details summary="See what’s included">
              <p>Here’s what’s included:</p>

              <List bullet>
                <ListItem>Legal work</ListItem>

                {(this.state.transactionType === "BuyingAndSelling" || this.state.transactionType === "Buying") && (
                  <>
                    <ListItem>
                      Searches: local, water and drainage, and environmental reports – £147.78, included in our initial
                      fee
                    </ListItem>
                    <ListItem>Chancel liability insurance – £13.00, included in our initial fee</ListItem>
                  </>
                )}
                <ListItem>ID checks – £6 per person, included in our legal fee</ListItem>
                <ListItem>Bank transfers – £20 each, included in our legal fee</ListItem>

                {(this.state.transactionType === "BuyingAndSelling" || this.state.transactionType === "Buying") && (
                  <>
                    <ListItem>Bankruptcy searches – £2 each, included in our legal fee</ListItem>
                    <ListItem>
                      Land Registry ‘official search with priority’ – £3 each, included in our legal fee
                    </ListItem>
                  </>
                )}
                <ListItem>Land Registry documents – £3 each, included in our legal fee</ListItem>
                <ListItem>Handling their mortgage</ListItem>

                {(this.state.transactionType === "BuyingAndSelling" || this.state.transactionType === "Buying") && (
                  <>
                    {!this.state.buyingDetails.clientIsCompany && (
                      <ListItem>Claiming their Help to Buy or Lifetime ISA bonus</ListItem>
                    )}
                    <ListItem>Filing their Stamp Duty return</ListItem>
                  </>
                )}
                <ListItem>VAT</ListItem>
                <ListItem>Regular email updates</ListItem>
                <ListItem>Telephone support</ListItem>
              </List>

              <p>
                If the property has a managing agent, your client will probably have to pay the agent some fees. Talk to
                the managing agent for more information.
              </p>
            </Details>

            {(this.state.transactionType === "Buying" || this.state.transactionType === "BuyingAndSelling") && (
              <>
                {!this.state.buyingDetails.clientIsCompany && (
                  <p>
                    We’ve quoted Stamp Duty as a UK resident at the full market value of the property. For a more
                    detailed estimate, visit{" "}
                    <a href="https://www.gov.uk/stamp-duty-land-tax" target="_blank" rel="noreferrer">
                      www.gov.uk/stamp-duty-land-tax
                    </a>
                  </p>
                )}
                {this.state.buyingDetails.clientIsCompany && (
                  <p>
                    We’ve calculated the Stamp Duty due at the full corporate rate and at the full market value of the
                    property. For a more detailed Stamp Duty estimate, visit{" "}
                    <a href="https://www.gov.uk/stamp-duty-land-tax" target="_blank" rel="noreferrer">
                      www.gov.uk/stamp-duty-land-tax
                    </a>
                  </p>
                )}
              </>
            )}
          </Card>
        )}

        <Image src={imageRelaxingOnSofa} fullWidth />
      </React.Fragment>
    );
  }
}

export default ReferrerQuoteDetailsPage;
